<template>
  <div class="main-content-container overflow-hidden">
    <PageTitle pageTitle="Customers" subTitle="Management" />

    <div class="card bg-white border-0 rounded-3 mb-4">
      <div class="card-body p-0">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 p-4">
          <form class="position-relative table-src-form me-0" @submit.prevent>
            <input type="text" class="form-control" placeholder="Search customers..." v-model="searchQuery" />
            <i class="material-symbols-outlined position-absolute top-50 start-0 translate-middle-y">search</i>
          </form>
          <RouterLink to="/customers/create" class="btn btn-outline-primary py-1 px-2 px-sm-4 fs-14 fw-medium rounded-3 hover-bg">
            <span class="py-sm-1 d-block">
              <i class="ri-add-line d-none d-sm-inline-block me-1"></i>
              <span>Add New Customer</span>
            </span>
          </RouterLink>
        </div>

        <div class="default-table-area style-two default-table-width">
          <div class="table-responsive">
            <table class="table align-middle">
              <thead>
                <tr>
                  <th scope="col">ID</th>
                  <th scope="col">Phone Number</th>
                  <th scope="col">Name</th>
                  <th scope="col">Free Tokens</th>
                  <th scope="col">Created At</th>
                  <th scope="col" v-if="isSuperAdmin">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td colspan="6" class="text-center py-4">Loading...</td>
                </tr>
                <tr v-else-if="filteredCustomers.length === 0">
                  <td colspan="6" class="text-center py-4">No customers found.</td>
                </tr>
                <tr v-else v-for="customer in filteredCustomers" :key="customer.customer_id">
                  <td>#{{ customer.customer_id }}</td>
                  <td class="text-body">{{ customer.phone_number }}</td>
                  <td>
                    <h6 class="mb-0 fs-14 fw-medium">{{ customer.name || '-' }}</h6>
                  </td>
                  <td class="text-body">{{ customer.free_tokens }}</td>
                  <td class="text-body">{{ new Date(customer.created_at).toLocaleDateString() }}</td>
                  <td v-if="isSuperAdmin">
                    <div class="d-flex align-items-center gap-1">
                      <RouterLink :to="`/customers/${customer.customer_id}/edit`" class="ps-0 border-0 bg-transparent lh-1 position-relative top-2">
                        <i class="material-symbols-outlined fs-16 text-body">edit</i>
                      </RouterLink>
                      <button @click="deleteCustomer(customer.customer_id)" class="ps-0 border-0 bg-transparent lh-1 position-relative top-2">
                        <i class="material-symbols-outlined fs-16 text-danger">delete</i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import PageTitle from '@/components/Common/PageTitle.vue';
import customerService from '@/services/customerService';

export default defineComponent({
  name: 'CustomerListPage',
  components: {
    PageTitle
  },
  setup() {
    const customers = ref<any[]>([]);
    const loading = ref(true);
    const searchQuery = ref('');

    const isSuperAdmin = computed(() => {
      const userStr = localStorage.getItem('user');
      if (!userStr) return false;
      try {
        const user = JSON.parse(userStr);
        return user.role === 'super_admin';
      } catch (e) {
        return false;
      }
    });

    const fetchCustomers = async () => {
      loading.value = true;
      try {
        const response = await customerService.getAll();
        customers.value = response.data;
      } catch (error) {
        console.error('Error fetching customers:', error);
      } finally {
        loading.value = false;
      }
    };

    const deleteCustomer = async (id: number) => {
      if (confirm('Are you sure you want to delete this customer?')) {
        try {
          await customerService.delete(id);
          fetchCustomers();
        } catch (error) {
          console.error('Error deleting customer:', error);
        }
      }
    };

    const filteredCustomers = computed(() => {
      if (!searchQuery.value) return customers.value;
      const q = searchQuery.value.toLowerCase();
      return customers.value.filter(c => 
        c.phone_number.toLowerCase().includes(q) || 
        (c.name && c.name.toLowerCase().includes(q))
      );
    });

    onMounted(() => {
      fetchCustomers();
    });

    return {
      customers,
      loading,
      searchQuery,
      filteredCustomers,
      deleteCustomer,
      isSuperAdmin
    };
  }
});
</script>
